<template>
  <div class="m-tabs">
    <div class="m-tabs__container">
      <div
        class="m-tabs__indicator m-tabs__indicator--left"
        v-if="scrollLeft"
        :class="{ 'side-active': iconLeft }"
        @click="scrollTo('left')">
        <ChevronLeftIcon class="m-tabs__indicator--left__chevron" />
        <FontIcon :name="iconLeft" class="m-tabs__indicator--left__active-icon" v-if="iconLeft" />
      </div>
      <div
        class="m-tabs__indicator m-tabs__indicator--right"
        v-if="scrollRight"
        :class="{ 'side-active': iconRight }"
        @click="scrollTo('right')">
        <ChevronRightIcon class="m-tabs__indicator--right__chevron" />
        <FontIcon :name="iconRight" class="m-tabs__indicator--right__active-icon" v-if="iconRight" />
      </div>
      <div class="m-tabs__tabs" @scroll="determineSideIcons()" ref="tabs" v-if="tabs">
        <a
          v-for="(tab, slotName) in tabs"
          :key="slotName"
          :class="`m-tabs__tabs__tab${slotName === activeSlotName ? ' m-tabs__tabs__tab--active' : ''}`"
          :data-id="slotName"
          :ref="`tab-${slotName}`"
          @click="selectTab(slotName)">
            <FontIcon :name="tab.icon" v-if="tab.icon" />
            <span>{{ tab.title }}</span>
        </a>
      </div>
    </div>
    <div class="m-tabs__content">
      <div v-for="(slot, name) in $slots" :key="name">
        <slot :name="name" v-if="activeSlotName === name" />
      </div>
    </div>
  </div>
</template>

<script>
import FontIcon from '@/components/Icon.vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VTabs',
  introduction: 'Shows slots, one at a time, through a series of scrollable tabs.',
  components: {
    FontIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  data() {
    return {
      tabs: [],
      activeTab: null,
      iconLeft: null,
      iconRight: null,
      scrollLeft: false,
      scrollRight: false,
      activeSlotName: null,
    };
  },
  created() {
    window.addEventListener('resize', this.determineSideIcons);
  },
  mounted() {
    this.tabs = Object.keys(this.$slots).reduce((result, slotName) => {
      const slot = this.$slots[slotName][0];
      result[slotName] = slot.data.attrs; // eslint-disable-line no-param-reassign
      return result;
    }, {});

    this.activeSlotName = Object.keys(this.$slots)[0]; // eslint-disable-line

    this.$nextTick(() => {
      this.determineSideIcons();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.determineSideIcons);
  },
  methods: {
    selectTab(selectedSlotName) {
      this.iconLeft = null;
      this.iconRight = null;
      // this.activeTab = tabSelected;
      //
      // const id = parseInt($event.target.getAttribute('data-id'), 10);
      // this.tabs.forEach((tab, index) => {
      //   const item = tab;
      //   item.active = index === id;
      // });

      this.activeSlotName = selectedSlotName;

      // this.$nextTick(() => {
      this.determineSideIcons();
      // });
      this.$emit('showing', selectedSlotName);
    },
    determineSideIcons() {
      this.determineScrollDirections();
      const tabDiv = this.$refs.tabs;
      const el = this.$refs[`tab-${this.activeSlotName}`][0];

      if (tabDiv.scrollLeft >= (el.offsetLeft + el.offsetWidth)) {
        this.iconLeft = this.tabs[this.activeSlotName].icon;
      } else if ((el.offsetLeft) >= (tabDiv.scrollLeft + tabDiv.offsetWidth)) {
        this.iconRight = this.tabs[this.activeSlotName].icon;
      } else {
        this.iconLeft = null;
        this.iconRight = null;
      }
    },
    determineScrollDirections() {
      const tabDiv = this.$refs.tabs;
      if (tabDiv.scrollLeft > 10) {
        this.scrollLeft = true;
      } else {
        this.scrollLeft = false;
      }

      if ((tabDiv.scrollLeft + tabDiv.offsetWidth + 10) <= (tabDiv.scrollWidth)) {
        this.scrollRight = true;
      } else {
        this.scrollRight = false;
      }
    },
    scrollTo(direction) {
      if (direction === 'right') {
        this.$refs.tabs.scrollLeft += 100;
      } else if (direction === 'left') {
        this.$refs.tabs.scrollLeft -= 100;
      }
    },
  },
};
</script>

<style scoped>
.m-tabs {
  border: 1px solid var(--color-border);
}

.m-tabs__container {
  overflow: hidden;
  height: 50px;
  margin-bottom: -3px;
  background: #fff;
}

.m-tabs__tabs {
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  padding-bottom: 60px;
  position: relative;
}

.m-tabs__tabs__tab {
  display: inline-block;
  padding: var(--spacing-base);
  cursor: pointer;
  margin-bottom: -3px;
  min-width: 80px;
  line-height: 1;
  text-decoration: none;
  color: #555;
}

.m-tabs__tabs__tab:hover {
  border-bottom: 3px solid var(--signable-secondary-2-shade-2);
}

.m-tabs__tabs__tab--active {
  border-bottom: 3px solid var(--signable-primary-1);
}

.m-tabs__tabs__tab--active:hover {
  border-bottom: 3px solid var(--signable-primary-1);
}

.m-tabs__tabs__tab svg {
  float: left;
  margin-right: var(--spacing-small);
  height: 16px;
  width: 16px;
}

.m-tabs__tabs__tab svg,
.m-tabs__tabs__tab span {
  pointer-events: none;
}

.m-tabs__content {
  padding: var(--spacing-base);
  border-top: 3px solid var(--color-border);
  background: #fff;
}

.m-tabs__indicator {
  padding: var(--spacing-small);
  margin-top: var(--spacing-xs);
  height: calc(100% - 13px);
  max-width: 50px;
  transition: all 0.4s ease;
  position: relative;
}

.m-tabs__indicator--left {
  padding-left: var(--spacing-base);
  float: left;
  border-right: 1px solid var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--left__chevron {
  position: absolute;
  left: var(--spacing-xs);
  color: var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--left__active-icon {
  margin-left: var(--spacing-small);
}

.m-tabs__indicator--right {
  padding-right: var(--spacing-base);
  float: right;
  border-left: 1px solid var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--right__chevron {
  position: absolute;
  right: var(--spacing-xs);
  color: var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--right__active-icon {
  margin-right: var(--spacing-small);
}

.side-active:after {
  content: '';
  width: 100%;
  height: 3px;
  background: var(--signable-primary-1);
  position: absolute;
  bottom: -8px;
  left: 0;
}
</style>
